import { useEffect, useRef, type ReactNode } from "react";

/** 轻量滚动浮现容器：进入视口后淡入上移 */
export function Reveal({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            node.classList.add("is-in");
            io.unobserve(node);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${className}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
}

/** 分区标题：眉标 + 大标题 + 描述 */
export function SectionHeading({
  eyebrow,
  title,
  desc,
  tone = "light",
}: {
  eyebrow: string;
  title: ReactNode;
  desc?: string;
  tone?: "light" | "cream";
}) {
  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3">
        <span className="h-5 w-1.5 rounded-full bg-primary-500" />
        <span className="text-sm font-semibold tracking-[0.2em] text-primary-600 uppercase">{eyebrow}</span>
      </div>
      <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">{title}</h2>
      {desc ? (
        <p className={`mt-4 text-sm leading-relaxed md:text-base ${tone === "cream" ? "text-foreground-700/80" : "text-foreground-700"}`}>
          {desc}
        </p>
      ) : null}
    </div>
  );
}

/** 分区切换的胶囊标签 */
export function Pill({ active = false, children }: { active?: boolean; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
        active ? "bg-foreground-950 text-background-50" : "bg-secondary-100 text-secondary-900"
      }`}
    >
      {children}
    </span>
  );
}